import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import portfolioData from "../../data/portfolio.json";
import projectDefaultImg from "../../image/Default Project.png";
import { ArrowUpRight, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/api";

/* ─────────────────────────────────────────────
 GRID ITEM CARD (3D Tilt)
───────────────────────────────────────────── */
const GridItem = ({ item }) => {
  const cardRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;

      gsap.to(el, {
        rotateY: x * 10,
        rotateX: -y * 10,
        duration: 0.5,
        ease: "power2.out",
        transformPerspective: 1000,
        transformOrigin: "center center",
      });
    };

    const handleLeave = () => {
      gsap.to(el, {
        rotateY: 0,
        rotateX: 0,
        duration: 0.7,
        ease: "elastic.out(1, 0.4)",
      });
    };

    el.addEventListener("mousemove", handleMove);
    el.addEventListener("mouseleave", handleLeave);

    return () => {
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  // Format Date safely to extract year context
  const projectYear = item.start_date
    ? new Date(item.start_date).getFullYear()
    : "Present";
  const expertiseTags = Array.isArray(item.tags) ? item.tags : [];
  const navigate = useNavigate();
  return (
    <div className="transition-all duration-700 ease-out transform-gpu opacity-100 scale-100">
      <div
        onClick={() => {
          navigate(`/projects/${item.slug}`);
        }}
        ref={cardRef}
        className="group relative rounded-2xl overflow-hidden glass border border-slate-200 aspect-square cursor-pointer bg-white shadow-lg perspective-1000 block"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent z-10 opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
          <img
            src={item.image ? `${backendUrl}${item.image}` : projectDefaultImg}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
          />
        </div>

        {/* Overlay Content */}
        <div className="absolute inset-0 z-20 p-6 flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest backdrop-blur-md border border-white/20 text-white shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-[-10px] group-hover:translate-y-0 bg-indigo-600/50">
              {item.category}
            </span>
            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300 delay-100">
              <ArrowUpRight size={18} className="text-white" />
            </div>
          </div>

          <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            <p className="text-[10px] font-bold tracking-[0.2em] text-slate-300 uppercase mb-2">
              {item.client || "Creative Initiative"} • {projectYear}
            </p>
            <h3 className="font-display font-bold text-xl md:text-2xl text-white leading-tight mb-3">
              {item.title}
            </h3>

            <div className="flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150">
              {item.tags.map((tag, i) => (
                <span
                  key={i}
                  className="text-[9px] uppercase tracking-wider text-slate-300 border border-white/20 px-2 py-1 rounded bg-black/20"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN GRID COMPONENT
───────────────────────────────────────────── */
const ProjectGrid = () => {
  const { grid } = portfolioData;
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 6; // Changed to multiple of 3 to balance out responsive column cards grids perfectly

  // Reset pagination sequence down to index 1 whenever active category context switches
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  // 1. React Query setup tracking page and activeCategory inside QueryKeys
  const {
    data: projectResponse,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["projects", limit, currentPage, activeCategory],
    queryFn: () => {
      const params = {
        page: currentPage,
        limit,
        isActive: true,
      };

      // If category is not "All", apply it as a search metric matching your controller layer route setup
      if (activeCategory !== "All") {
        params.category = activeCategory.toLowerCase();
      }

      return api
        .get("/api/projects/public", { params })
        .then((res) => res.data);
    },
    keepPreviousData: true, // Retains smooth transitions during layout rendering
  });

  const projectsList = projectResponse?.projects || [];
  const totalPages = projectResponse?.totalPages || 1;

  const sectionRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current.children,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 80%",
          },
        },
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-py bg-slate-50 relative border-t border-slate-200 py-16"
    >
      <div className="container-fluid px-4 md:px-8 max-w-7xl mx-auto">
        <div
          ref={headerRef}
          className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16"
        >
          <div className="max-w-xl">
            <div className="section-label inline-flex mb-4 text-xs font-bold tracking-widest uppercase text-slate-400 gap-1 items-center">
              <Filter size={12} className="text-indigo-500" />
              {grid.sectionLabel}
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl text-slate-800 tracking-tight leading-none mb-4">
              {grid.headline}{" "}
              <span className="text-indigo-600">{grid.headlineAccent}</span>
            </h2>
            <p className="text-slate-600 text-base md:text-lg">
              {grid.description}
            </p>
          </div>

          {/* Categories Filter Controls */}
          <div className="flex flex-wrap gap-2 md:max-w-md justify-start md:justify-end">
            {grid.categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 border ${
                  activeCategory === cat
                    ? "bg-indigo-600 text-white border-indigo-500 shadow-md"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100 hover:text-slate-800"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Display Rendering States */}
        {isFetching ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-3">
            <div className="w-9 h-9 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs font-mono tracking-wider animate-pulse">
              Loading Platform Projects...
            </p>
          </div>
        ) : isError || projectsList.length === 0 ? (
          <div className="text-center py-24 text-slate-400 font-mono text-xs border border-dashed border-slate-200 rounded-2xl bg-white">
            No projects cataloged under this criteria sequence.
          </div>
        ) : (
          <>
            {/* Grid Layout Cards mapping server payloads */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {projectsList.map((item) => (
                <GridItem key={item.id} item={item} />
              ))}
            </div>

            {/* ── INTERACTIVE PAGINATION CONTROLS CONTROLLER ROW ── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12 pt-6 border-t border-slate-200/60">
                {/* Previous Button */}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>

                {/* Page Index Numbers */}
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(
                  (pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 text-xs font-bold font-mono rounded-xl border transition ${
                        currentPage === pageNum
                          ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {String(pageNum).padStart(2, "0")}
                    </button>
                  ),
                )}

                {/* Next Button */}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProjectGrid;
